"use client";

import { useSidebar } from "@/contexts/sidebar-context";
import { useLanguage } from "@/contexts/language-context";
import { PanelLeftOpen, PanelLeftClose, PanelRightOpen, PanelRightClose, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/auth-context";
import { SessionAccessDialog } from "@/components/session-access-dialog";
import { useNewChat } from "@/hooks/use-new-chat";

export default function ShowSidebarButton() {
  const { isOpen, openSidebar, closeSidebar } = useSidebar();
  const { t, isRTL, language } = useLanguage();
  const { user } = useAuth();
  const newChat = useNewChat();
  const [hasMounted, setHasMounted] = useState(false);

  const label = isOpen ? t('common.hideSidebar') : t('common.showSidebar');
  const togglePositionClass = isRTL ? (isOpen ? 'right-[232px]' : 'right-[132px]') : isOpen ? 'left-[232px]' : 'left-[132px]';
  const newChatPositionClass = isRTL ? 'right-[162px]' : 'left-[162px]';
  const actionButtonClass = 'fixed top-3 z-[60] rounded-md p-2 transition-colors hover:bg-[#034854]/10';

  useEffect(() => {
    setHasMounted(true);
  }, []);

  if (!hasMounted) {
    return null;
  }

  const renderIcon = () => {
    if (isRTL) {
      return isOpen ? (
        <PanelRightClose className="w-6 h-6 text-[#cccccc]" />
      ) : (
        <PanelRightOpen className="w-6 h-6 text-[#cccccc]" />
      );
    } else {
      return isOpen ? (
        <PanelLeftClose className="w-6 h-6 text-[#cccccc]" />
      ) : (
        <PanelLeftOpen className="w-6 h-6 text-[#cccccc]" />
      );
    }
  };

  return (
    <>
      <button
        onClick={isOpen ? closeSidebar : openSidebar}
        className={cn(actionButtonClass, togglePositionClass, 'cursor-pointer')}
        aria-label={label}
        title={label}>
        {renderIcon()}
      </button>

      {!isOpen && (
        <button
          type="button"
          className={cn(
            actionButtonClass,
            newChatPositionClass,
            'hidden md:block',
            newChat.isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
          )}
          onClick={() => newChat.startNewChat()}
          disabled={newChat.isDisabled}
          aria-label={t('sidebar.newChat')}
          title={newChat.buttonTitle ?? t('sidebar.newChat')}>
          <Plus className="w-6 h-6 text-[#cccccc]" />
        </button>
      )}

      <SessionAccessDialog
        open={newChat.limitDialog.open}
        onOpenChange={newChat.setLimitDialogOpen}
        variant="limit"
        language={language}
        level={user?.level ?? 'L0'}
        limit={newChat.limitDialog.limit}
      />
    </>
  );
}
